import { BackendError, Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';

import { homeGlobalState } from './atom';
import { screenName } from './constants';
import {
  FundraisingSummaryResponseNormalized,
  FundraisingAnalysisChartSuccessResponseNormalized,
  FundraisingAucResponseNormalized,
  FundraisingConsolidatedSuccessResponseNormalized,
  FundraisingAssetEvolutionResponseNormalized,
  FundraisingAssetEvolutionPeriodOptions,
  FundraisingFinancialConsolidatedSuccessResponseNormalized,
  FundraisingFinancialChartResponseNormalized,
  ResponsesFundraisingTopProductsResponseNormalized,
} from './types';
import { buildStackDataKey } from './utils';

export const changeHomeValueVisibility = selector<boolean>({
  key: 'Home/changeHomeValueVisibility',
  set: ({ set, get }, newValue) => {
    const globalState = get(homeGlobalState);
    set(homeGlobalState, { ...globalState, valueVisibility: newValue as boolean });
  },
  get: ({ get }) => {
    return get(homeGlobalState).valueVisibility;
  },
});

export const getHomeValueVisibility = selector({
  key: 'Home/getHomeValueVisibility',
  get: ({ get }) => {
    return get(homeGlobalState).valueVisibility;
  },
});

export const yearsQuery = selector({
  key: 'Home/Clients/YearsQuery',
  get: async () => {
    const { data } = await Service.Home.V1.getConversionYearAvailable({
      screenName,
    });

    return data.year?.sort((a, b) => b - a)?.map((year) => year.toString()) || [];
  },
});

export const firstYear = selector({
  key: 'Home/Clients/FirstYear',
  get: ({ get }) => {
    const years = get(yearsQuery);
    return years?.[0] || new Date().getFullYear().toString();
  },
});

export const fundraisingSummaryQuery = selectorFamily<
  FundraisingSummaryResponseNormalized[] | null | undefined,
  { summaryType }
>({
  key: 'Home/FundraisingSummary/Query',
  get:
    ({ summaryType }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      try {
        const { data } = await Service.Home.V1.getFundraisingSummary({
          summaryType,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          screenName: 'FundraisingSummary',
        });

        if (!data) {
          return data;
        }

        const normalizedFundraisingSummary: FundraisingSummaryResponseNormalized[] = data.map(
          (summaryItem) => ({
            ...summaryItem,
            relatedDateNormalized: summaryItem.relatedDate
              ? Utils.Mask.DateFormat(summaryItem.relatedDate, 'dd/MM/yyyy')
              : '--',
            customerNameNormalized: summaryItem.customerName || '',
            productNameNormalized: summaryItem.productName || '',
            productDescriptionNormalized: summaryItem.productDescription || '',
            operationValueNormalized: summaryItem.operationValue || 0,
            customerBalanceNormalized: summaryItem.customerBalance || 0,
          })
        );

        return normalizedFundraisingSummary || [];
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return [];
        }

        throw error;
      }
    },
});

export const fundraisingAnalysisQuery = selector<
  FundraisingAnalysisChartSuccessResponseNormalized | null | undefined
>({
  key: 'Home/FundraisingAnalysis/Query',
  get: async ({ get }) => {
    let total = 0;
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data: chartData } = await Service.Home.V1.getFundraisingAnalysisChart({
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      screenName,
    });

    let chartDataArray =
      chartData?.chartData?.map((item) => {
        total += item.netFundraisingValue;

        const referenceDate = item.referenceDate
          ? new Date(`${item.referenceDate}-01 00:00:00`)
          : undefined;
        const referenceDateNormalized = referenceDate
          ? referenceDate.toLocaleString('pt-BR', { month: 'long' })
          : '--';

        return {
          ...item,
          referenceDateNormalized,
          referenceDateFullNormalized: `${referenceDateNormalized} - ${referenceDate?.getFullYear()}`,
          referenceDateShortNormalized: referenceDate
            ? referenceDate.toLocaleString('pt-BR', { month: 'short' })
            : '--',
          contributionValueNormalized: Utils.Mask.Money(item.contributionValue),
          withdrawValueNormalized: Utils.Mask.Money(item.withdrawValue),
          netFundraisingValueNormalized: Utils.Mask.Money(item.netFundraisingValue),
        };
      }) ?? [];

    if (chartDataArray.length > 0) {
      const maxCheck = ['contributionValue', 'withdrawValue', 'netFundraisingValue'];

      chartDataArray = buildStackDataKey('contributionValue', maxCheck, chartDataArray);
      chartDataArray = buildStackDataKey('withdrawValue', maxCheck, chartDataArray);
      chartDataArray = buildStackDataKey('netFundraisingValue', maxCheck, chartDataArray);
    }

    return {
      ...chartData,
      total,
      totalNormalized: Utils.Mask.Money(total),
      startDateNormalized: Utils.Mask.DateFormat(chartData.startDate, 'dd/MM/yyyy'),
      endDateNormalized: Utils.Mask.DateFormat(chartData.endDate, 'dd/MM/yyyy'),
      chartData: chartDataArray,
    };
  },
});

export const fundraisingAucQuery = selector({
  key: 'Home/FundraisingAuc/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    try {
      const { data } = await Service.Home.V1.getFundraisingAuc({
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName: 'auc-consolidated',
      });

      const normalizedData: FundraisingAucResponseNormalized = {
        ...data,
        currentDateNormalized: Utils.Mask.DateFormat(data.currentDate, 'dd/MM/yyyy'),
        totalValueNormalized: data.totalValue || 0,
      };

      return { data: normalizedData };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: undefined };
      }

      throw error;
    }
  },
});

export const fundraisingConsolidatedQuery = selector({
  key: 'Home/Consolidated/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    try {
      const { data } = await Service.Home.V1.getFundraisingConsolidated({
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName: 'FundraisingSummary',
      });

      if (!data) {
        return data;
      }

      const normalizedData: FundraisingConsolidatedSuccessResponseNormalized = {
        ...data,
        startDateNormalized: data.startDate
          ? Utils.Mask.DateFormat(data.startDate, 'dd/MM/yyyy')
          : '--',
        endDateNormalized: data.endDate ? Utils.Mask.DateFormat(data.endDate, 'dd/MM/yyyy') : '--',
      };

      return { data: normalizedData };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: undefined };
      }

      throw error;
    }
  },
});

export const assetEvolutionQuery = selectorFamily<
  FundraisingAssetEvolutionResponseNormalized | null | undefined,
  { periodFilter: FundraisingAssetEvolutionPeriodOptions }
>({
  key: 'Home/AssetEvolutionQuery',
  get:
    ({ periodFilter }) =>
    async ({ get }) => {
      const advisorId = get(advisorIdToApisSelector);
      const { data } = await Service.Home.V1.getFundraisingAssetEvolution({
        periodFilter,
        advisorId,
        screenName: 'AssetEvolution',
      });

      if (!data) {
        return data;
      }

      const normalizedData: FundraisingAssetEvolutionResponseNormalized = {
        ...data,
        startDateNormalized: Utils.Mask.DateFormat(data.startDate, 'dd/MM/yyyy'),
        endDateNormalized: Utils.Mask.DateFormat(data.endDate, 'dd/MM/yyyy'),
        currentAssetNetValueNormalized: Utils.Mask.Money(data.currentAssetNetValue),
        chartData:
          data.chartData?.map((item) => ({
            ...item,
            referenceDateNormalized: Utils.Mask.DateFormat(item.referenceDate, 'dd/MM'),
            referenceDateFullNormalized: Utils.Mask.DateFormat(item.referenceDate, 'dd/MM/yyyy'),
            assetNetValueNormalized: Utils.Mask.Money(item.assetNetValue),
          })) ?? [],
      };

      return normalizedData;
    },
});

export const TopProductsQuery = selector<
  ResponsesFundraisingTopProductsResponseNormalized | null | undefined
>({
  key: 'Home/TopProducts/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    try {
      const { data } = await Service.Home.V1.getFundraisingTopProducts({
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName: 'top-products',
      });

      if (!data || !data.products) {
        return undefined;
      }

      const maxAmount = data.products.reduce((max, item) => {
        const amount = item.amount ?? 0;
        return amount > max ? amount : max;
      }, 0 as number);

      const topProductsDataNormalized = data.products.map((item) => ({
        amount: item.amount ?? 0,
        product: item.product,
        type: String(item.category).toLocaleLowerCase(),
        maxAmount,
      }));

      const normalizedData: ResponsesFundraisingTopProductsResponseNormalized = {
        ...data,
        relatedDateNormalized: Utils.Mask.DateFormat(data.relatedDate, 'dd/MM/yyyy'),
        products: topProductsDataNormalized,
      };

      return normalizedData;
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return undefined;
      }

      throw error;
    }
  },
});

export const financialAnalysisAverageTicketBigNumbersQuery = selector<
  FundraisingFinancialConsolidatedSuccessResponseNormalized | null | undefined
>({
  key: 'Home/FinancialAnalysisAverageTicketBigNumbers/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.Home.V1.getFundraisingFinancialConsolidated({
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      screenName: 'financial-analysis-average-tocket',
    });

    if (!data) {
      return data;
    }

    return {
      ...data,
      monthOverMonthPercentageNormalized: Utils.Mask.Percentage(data.monthOverMonthPercentage),
      monthToDateValueNormalized: Utils.Mask.Money(data.monthToDateValue),
      previousMonthValueNormalized: Utils.Mask.Money(data.previousMonthValue),
      previousQuarterValueNormalized: Utils.Mask.Money(data.previousQuarterValue),
      previousWeekValueNormalized: Utils.Mask.Money(data.previousWeekValue),
      previousYearValueNormalized: Utils.Mask.Money(data.previousYearValue),
      quarterToDateValueNormalized: Utils.Mask.Money(data.quarterToDateValue),
      quarterOverQuarterPercentageNormalized: Utils.Mask.Percentage(
        data.quarterOverQuarterPercentage
      ),
      weekOverWeekPercentageNormalized: Utils.Mask.Percentage(data.weekOverWeekPercentage),
      weekToDateValueNormalized: Utils.Mask.Money(data.weekToDateValue),
      yearOverYearPercentageNormalized: Utils.Mask.Percentage(data.yearOverYearPercentage),
      yearToDateValueNormalized: Utils.Mask.Money(data.yearToDateValue),
    };
  },
});

export const financialAnalysisAverageTicketChartQuery = selector<
  FundraisingFinancialChartResponseNormalized[] | null | undefined
>({
  key: 'Home/FinancialAnalysisAverageTicketChart/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.Home.V1.getFundraisingFinancialChart({
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      screenName: 'financial-analysis-average-tocket',
    });

    if (!data) {
      return data;
    }

    let chartDataArray = data.map((item) => ({
      ...item,
      currentDateNormalized: Utils.Mask.DateFormat(item.currentDate, 'MMM/yy'),
      currentDateSmallNormalized: Utils.Mask.DateFormat(item.currentDate, 'MMM')[0].toUpperCase(),
      currentDateFullNormalized: Utils.Mask.DateFormat(item.currentDate, 'MMMM - yyyy'),
      ticketAverageValueNormalized: Utils.Mask.Money(item.ticketAverageValue),
    }));

    if (chartDataArray.length > 0) {
      const maxCheck = ['ticketAverageValue'];

      chartDataArray = buildStackDataKey('ticketAverageValue', maxCheck, chartDataArray);
    }

    return chartDataArray;
  },
});
